import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import ImageUpload from '../../../components/ImageUpload';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    session_duration_mins: '',
    is_icebox_room: true,
    status: true,
  });

  useEffect(() => {
    if (id) getSingleData();
  }, [id]);

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/rooms/${id}`,
      });
      const data = response.data;
      if (data.status) {
        setFormData({
          title: data.data.title || '',
          session_duration_mins: data.data.session_duration_mins ?? '',
          is_icebox_room: !!data.data.is_icebox_room,
          status: !!data.data.status,
        });
        setPreviewUrl(data.data.image || null);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (name, file) => {
    setImageFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setIsLoader(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('session_duration_mins', formData.session_duration_mins);
      data.append('is_icebox_room', formData.is_icebox_room);
      data.append('status', formData.status);
      if (imageFile) data.append('image', imageFile);

      const response = await ApiService.request({
        method: 'POST',
        url: `admin/rooms/createOrUpdate/${id || ''}`,
        data,
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/admin/rooms');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit Room' : 'Add Room'}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/rooms" className="text-muted text-hover-primary">Rooms</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body py-6">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-6">
                    <label className="required form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="e.g. Ice Bath Room 1"
                    />
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Session Duration (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      name="session_duration_mins"
                      value={formData.session_duration_mins}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="e.g. 60"
                    />
                    <div className="form-text">Length of one bookable slot for this room.</div>
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Room Image</label>
                    <ImageUpload
                      name="image"
                      onImageChange={handleImageChange}
                      defaultPreviewUrl={previewUrl}
                      maxSizeMB={5}
                    />
                  </div>

                  <div className="col-md-3 mb-6">
                    <div className="form-check form-switch form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="is_icebox_room"
                        checked={formData.is_icebox_room}
                        onChange={handleChange}
                        id="is_icebox_room"
                      />
                      <label className="form-check-label" htmlFor="is_icebox_room">
                        Icebox room
                      </label>
                    </div>
                    <div className="form-text">Icebox rooms follow the icebox break time set in Settings.</div>
                  </div>

                  <div className="col-md-3 mb-6">
                    <div className="form-check form-switch form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="status"
                        checked={formData.status}
                        onChange={handleChange}
                        id="status"
                      />
                      <label className="form-check-label" htmlFor="status">
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3">
                  <Link to="/admin/rooms" className="btn btn-light">Cancel</Link>
                  <button type="submit" className="btn btn-base" disabled={isLoader}>
                    {isLoader ? <Loader text="Saving" size="20" /> : (id ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUpdate;
